import {
  Button,
  AlertBox,
  DatePicker,
  SelectInput,
} from "@life-line-lab/components-react";
import React, { useState, useEffect, useMemo } from "react";
import { omitBy, isNull, isEmpty, uniqBy, omit } from "lodash";
import { useTranslation } from "react-i18next";
import errorHandler from "Utils/error-handler";
import { Col, Collapse, Row, TimePicker } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import moment from "moment";
import { CombineTaskSchema } from "./combine-task-validation";
const { Panel } = Collapse;

// ==============================================================
//   DateTime Handler
// ==============================================================
const DateTimeHandler = (pickupWindow) => {
  const disabledHours = () => {
    let hours: any = [];

    // openingTime and closingTime
    const startHour = moment(pickupWindow?.start)?.hour();
    const endHour = moment(pickupWindow?.end)?.hour() + 1; // start blocking the next hour

    // disable hours outside openingTime
    for (let i = 0; i < startHour; i++) {
      hours.push(i);
    }

    // disable hours outside closingTime
    for (let i = endHour; i <= 24; i++) {
      hours.push(i);
    }

    // if all hours are blocked, let the user choose any hour
    if (hours.length >= 25) return [];
    else return hours;
  };

  const disabledMinutes = (selectedHour) => {
    const minutes: any = [];

    const pickupStart = moment(pickupWindow?.start);
    const pickupEnd = moment(pickupWindow?.end);
    let startMinutes = 0;
    let endMinutes = 59;

    if (selectedHour === pickupStart.hour()) {
      // if openingTime is 10.30, we let them start from 30 (if 10 is selected)
      startMinutes = pickupStart.minutes();
    } else if (selectedHour === pickupEnd.hour()) {
      // if closingTime is 10.30, we let them end at 30 (if 10 is selected)
      endMinutes = pickupEnd.minutes() + 1;
    }

    for (let i = 0; i < startMinutes; i++) {
      minutes.push(i);
    }

    for (let i = endMinutes; i <= 59; i++) {
      minutes.push(i);
    }

    return minutes;
  };

  return {
    disabledHours,
    disabledMinutes,
  };
};

// ==============================================================
//   Form Handlers
// ==============================================================
const CombineTaskHandler = (props) => {
  const { initialError, setError, setInput, input, info } = props || {};

  const handlePickUpWindow = () => {
    let tempOpTime = { start: null, end: null };

    const allPickUpDates = info?.map((pickup) => ({
      timeFrom: pickup?.pickupTimeFrom,
      timeTo: pickup?.pickupTimeTo,
    }));

    for (let i = 0; i < allPickUpDates?.length; i++) {
      let pickup = allPickUpDates[i];
      let currentOpTime;

      // if a pickup has no time, disallow filling in
      if (!pickup?.timeFrom || !pickup?.timeTo) {
        break;
      }

      // otherwise, get the timeFrom and timeTo
      currentOpTime = { start: pickup?.timeFrom, end: pickup?.timeTo };

      // if currentOpTime is not empty, begin filling in
      if (!isEmpty(currentOpTime)) {
        //   if tempOpTime is null, fill it in
        if (tempOpTime.start === null) {
          tempOpTime = { ...currentOpTime };
        } else {
          // if currentOpTime.start > tempOpTime.start, fill in that time
          //   dont use .isBefore or .isAfter, because Moment contains dates
          if (
            moment(currentOpTime.start).hour() > moment(tempOpTime.start).hour()
          ) {
            tempOpTime = { ...tempOpTime, start: currentOpTime.start };
          }

          // if currentOpTime.end < tempOpTime.end, fill in that time
          if (
            moment(currentOpTime.end).hour() < moment(tempOpTime.end).hour()
          ) {
            tempOpTime = { ...tempOpTime, end: currentOpTime.end };
          }
        }
      } else {
        // clear tempOpTime to prevent users to fill
        tempOpTime = { start: null, end: null };
        break;
      }
    }

    // setting up of available time
    return tempOpTime;
  };

  const handleSelectChange = (value: string | string[] | {}, name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleTimeChange = (value: any) => {
    setError({ initialError });

    // value contains an array of 2 elements: [Moment(startTime), Moment(endTime)]
    setInput({ ...input, startTime: value[0], endTime: value[1] });
  };

  return {
    handlePickUpWindow,
    handleSelectChange,
    handleChange,
    handleTimeChange,
  };
};

// ==============================================================
//   Pickup Collapsible
// ==============================================================
const PickupContainer = ({ info }) => {
  const { t } = useTranslation();

  const handleListPickupWindow = () => {
    return (
      <>
        {info &&
          info?.map((pickup, index) => (
            <Row key={index} gutter={[8, 0]}>
              <Col span={2}>{index + 1}.</Col>
              <Col span={10}>{pickup?.pickupCode}</Col>
              <Col span={12}>
                <div>{`${moment(pickup?.pickupTimeFrom).format(
                  "hh:mm a",
                )} - ${moment(pickup?.pickupTimeTo).format("hh:mm a")}`}</div>
              </Col>
            </Row>
          ))}
      </>
    );
  };

  return (
    <Collapse defaultActiveKey={["1"]}>
      <Panel header={t("title.pickupWindow")} key={1} className="pu-panel">
        <div className="pu-panel-inner">
          <Row gutter={[8, 0]}>
            <Col flex="none" className="pickup-heading">
              {t("label.totalParcels")}:
            </Col>
            <Col flex="auto">
              {info?.reduce(
                (count, pickup) => count + parseInt(pickup?.noOfParcels),
                0,
              )}
            </Col>
          </Row>
          <Row gutter={[8, 0]}>
            <Col span={2} className="pickup-heading">
              {t("label.no")}
            </Col>
            <Col span={10} className="pickup-heading">
              {t("label.pickupCode")}
            </Col>
            <Col span={12} className="pickup-heading">
              {t("label.pickupWindow")}
            </Col>
            <Col span={24}>{handleListPickupWindow()}</Col>
          </Row>
        </div>
      </Panel>
    </Collapse>
  );
};

// ==============================================================
//   Customer Collapsible
// ==============================================================
const CustomerContainer = ({ info }) => {
  const { t } = useTranslation();

  const uniqueCustomerAndLocation = info?.map((pickup) => ({
    pickupCode: pickup?.pickupCode,
    pickupTasks: uniqBy(pickup?.pickupTasks, "merchantName"),
  }));

  const formatAddress = (address) => {
    return (
      <div>
        {address.street1 + ", "}
        {address.street2 ? address.street2 + ", " : ""}
        {address.postcode + " "}
        {address.city + ", "}
        {address.state}
      </div>
    );
  };

  return (
    <Collapse>
      <Panel
        header={t("title.customerAndLocation")}
        key={1}
        className="pu-panel"
      >
        <div className="pu-panel-inner">
          <Row gutter={[8, 8]}>
            {uniqueCustomerAndLocation?.map((pickup, index) => {
              return (
                <Col key={index}>
                  {index != 0 && <div className="om-divider pad"></div>}
                  <Row>
                    <Col span={24} className="pickup-heading">
                      {pickup?.pickupCode}
                    </Col>
                    <Col span={24}>
                      <Row gutter={[0, 4]}>
                        {pickup?.pickupTasks?.map((task, index) => (
                          <Col key={index}>
                            <Row>
                              <Col span={10}>{task?.merchantName}</Col>
                              <Col span={12}>
                                {formatAddress(task?.address)}
                              </Col>
                            </Row>
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  </Row>
                </Col>
              );
            })}
          </Row>
        </div>
      </Panel>
    </Collapse>
  );
};

// ==============================================================
//   Get Earliest Date
// ==============================================================
const getEarliestDate = (info) => {
  let earliestDate: any;
  const nextDate = moment().add(1, "days");

  for (let i = 0; i < info?.length; i++) {
    const pickupDate = moment(info[i]?.pickupDate);

    // for the first iteration
    if (!earliestDate) {
      // if the pickup date is after today, choose pickup date, otherwise current date
      earliestDate = pickupDate.isAfter(nextDate) ? pickupDate : nextDate;
      continue;
    }

    // if pickupdate is before earliest date, overwrite it
    if (pickupDate.isBefore(earliestDate)) {
      earliestDate = pickupDate.isAfter(nextDate) ? pickupDate : nextDate;
    }
  }

  return earliestDate;
};

// ==============================================================
//   Main Component
// ==============================================================
const CombineTaskModal = ({ info, onSubmit, onCancel }) => {
  const initialDate = getEarliestDate(info);
  const initialInput = {
    pickupDate: initialDate ?? moment().add(1, "days"),
    startTime: null,
    endTime: null,
    assignedDriver: "",
  };
  const assignedDriverOptions: any[] = useMemo(
    () =>
      info
        ?.filter((pickup: any) => pickup.driverData?.fullName)
        .reduce((unique: any[], pickup: any) => {
          const exists = unique.find(item => item.value === pickup.driverData._id);
          if (!exists) {
            unique.push({
              label: pickup.driverData.fullName,
              value: pickup.driverData._id,
            });
          }
          return unique;
        }, []) || [],
    [info]
  );
  
  const { t } = useTranslation();
  const initialError = {};
  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [isOperational, setIsOperational] = useState<boolean>(false);

  // enable confirm button if all fields are filled
  useEffect(() => {
    const validDriver =
      assignedDriverOptions.length > 0 ? Boolean(input.assignedDriver) : true;
    if (
      input.pickupDate &&
      input.startTime &&
      input.endTime &&
      validDriver
    ) {
      setIsOperational && setIsOperational(true);
    }
  }, [input]);

  //   Form Handlers
  // =======================================================
  const pickupFormObjects = { initialError, setError, setInput, input, info };
  const { handleTimeChange, handlePickUpWindow, handleSelectChange } =
    CombineTaskHandler(pickupFormObjects);

  //   Restricts time selection to working hours
  // =======================================================
  const [pickupWindow, setPickupWindow] = useState<any>(handlePickUpWindow());
  const { disabledHours, disabledMinutes } = DateTimeHandler(pickupWindow);

  const handleDateChange = (value: moment.Moment, name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value, startTime: null, endTime: null });
  };

  const handleSubmit = () => {
    try {
      // omit all unrequired fields
      const pickups = info?.map((pickupParcel) => ({
        ...omit(pickupParcel, [
          "_rev",
          "orders",
          "uniqueCustomersAndLocations",
          "totalLocations",
          "vehicleData",
          "driverData",
          "vehicleTypeData",
        ]),
        ...(input.assignedDriver && { assignedDriver: input.assignedDriver }),
        pickupTasks: pickupParcel.pickupTasks.map((pickupTask) =>
          omit(pickupTask, [
            "locationName",
            "merchantName",
            "address",
            "dropOffLocationName",
            "dropOffLocationAddress",
          ]),
        ),
      }));

      const valInput = {
        pickups: pickups,
        pickupDate: input.pickupDate
          ? moment(input.pickupDate).toISOString()
          : null,
        pickupTimeFrom: input.startTime
          ? moment(input.startTime).toISOString()
          : null,
        pickupTimeTo: input.endTime
          ? moment(input.endTime).toISOString()
          : null,
      };

      const value = CombineTaskSchema.validateSync(omitBy(valInput, isNull), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });

      onSubmit(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  return (
    <div className="pickup-modal">
      <div className="modal-content-title">
        {t("action.createPickUp").toLocaleUpperCase()}
      </div>
      <div className="pickup-content pickup-customer-summary">
        <PickupContainer info={info} />
      </div>
      <div className="pickup-content pickup-customer-summary">
        <CustomerContainer info={info} />
      </div>
      <div className="pickup-content">
        <Row gutter={[0, 16]}>
          {assignedDriverOptions.length > 0 && (
            <Col span={24}>
              <SelectInput
                showSearch
                label={t("action.assignDriver")}
                options={assignedDriverOptions || []}
                isRequired
                value={input.assignedDriver}
                name="assignedDriver"
                placeholder={t("txt.selectHere")}
                onChange={(value: string | string[]) =>
                  handleSelectChange(value, "assignedDriver")
                }
              />
              <AlertBox text={error.assignedDriver} />
            </Col>
          )}
          <Col span={24}>
            <DatePicker
              isRequired={true}
              isError={!!error.pickupDate}
              suffix={<CalendarOutlined />}
              label={t("label.pickupDate")}
              placeholder="YYYY-MM-DD"
              disabledDate={(nextDate: any) => {
                return nextDate && nextDate.valueOf() < Date.now();
              }}
              value={input.pickupDate ? moment(input.pickupDate) : null}
              onChange={(value: any) => handleDateChange(value, "pickupDate")}
            />
            <AlertBox text={error.pickupDate} />
          </Col>
          <Col span={24}>
            <TimePicker.RangePicker
              value={[input.startTime, input.endTime]}
              onChange={(value) => {
                handleTimeChange(value);
              }}
              disabledHours={disabledHours}
              disabledMinutes={disabledMinutes}
              format={"hh:mm a"}
              renderExtraFooter={() => (
                <div className="timepicker-footer">
                  If every hour is blocked, select PM first.
                </div>
              )}
            />
            <AlertBox text={error.pickupTimeFrom || error.pickupTimeTo} />
          </Col>
        </Row>
      </div>
      <div className="right modal-end-button">
        <Button
          isDisabled={!isOperational}
          type={"primary"}
          label={t("action.confirm")}
          onClick={() => {
            handleSubmit();
          }}
        />
        <Button
          className="cancel-btn"
          type={"default"}
          label={t("action.cancel")}
          onClick={() => {
            onCancel();
          }}
        />
      </div>
    </div>
  );
};

export default CombineTaskModal;
